import React, { useEffect, useRef, useState } from 'react';
import { Clock, X } from 'lucide-react';
import {
  PlanReviewStep,
  composePlanChangeMessage,
  formatEtaRange,
  stepKindIsAsk,
  stepKindLabel,
} from './planReview';

// ── Copy ─────────────────────────────────────────────────────────────────
const CARD_TITLE = "Here's my research plan";
const CARD_SUBCOPY =
  "Review the steps — edit any wording, remove what you don't need, or add a question. I'll run exactly this.";
// Once the plan is edited, "run exactly this" is no longer accurate: the changes go
// back to the planner, which produces a revised plan for another review. Say that.
const CARD_SUBCOPY_EDITED =
  "Review the steps — edit any wording, remove what you don't need, or add a question. I'll revise the plan with your changes and show it again before running.";
const STEP_PLACEHOLDER = 'Describe this step…';
const ADD_STEP_LABEL = '+ Add a step…';
const APPROVE_LABEL = 'Approve & run';
const SEND_CHANGES_LABEL = 'Send changes';
const REQUEST_CHANGES_LABEL = 'Request changes instead';
const FOOTER_HINT =
  "Reviewing the plan doesn't use a query. You can still just type changes in chat.";

// A step being edited in place. `originalLabel === null` marks a user-added step.
interface EditableStep {
  key: string;
  id: string;
  label: string;
  originalLabel: string | null;
  // Display only. Deliberately NOT part of what gets sent back: an edited label goes to the
  // planner verbatim as the next question, so the kind's copy must never touch `label`.
  kind?: string | null;
}

// The step's kind, named above its text. Rendered as its own element — see EditableStep.kind.
const StepKindLabel: React.FC<{ kind?: string | null }> = ({ kind }) => {
  const label = stepKindLabel(kind);
  if (!label) return null;
  return (
    <span
      className={`mb-0.5 inline-block text-[10px] font-bold uppercase tracking-[0.06em] ${
        stepKindIsAsk(kind) ? 'text-[#0b7f75]' : 'text-[#94a3b8]'
      }`}
    >
      {label}
    </span>
  );
};

interface PlanReviewCardProps {
  steps: PlanReviewStep[];
  etaMinutesMin?: number;
  etaMinutesMax?: number;
  /** Plan approved unchanged → run exactly these steps (the surface sends "go"). */
  onApprove: (steps: PlanReviewStep[]) => void;
  /** Plan edited → send the composed change description as a normal chat message. */
  onRequestChanges: (message: string) => void;
  /** "Request changes instead" — hand focus to the chat composer. */
  onFocusComposer?: () => void;
  /** Older/superseded cards render read-only (no editing, no actions). */
  disabled?: boolean;
}

const toEditable = (steps: PlanReviewStep[]): EditableStep[] =>
  steps.map((s) => ({
    key: `orig-${s.id}`,
    id: s.id,
    label: s.label,
    originalLabel: s.label,
    kind: s.kind,
  }));

const isStepEdited = (s: EditableStep): boolean =>
  s.originalLabel === null ? s.label.trim().length > 0 : s.label.trim() !== s.originalLabel.trim();

const NumberSquare: React.FC<{ n: number }> = ({ n }) => (
  <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-md bg-[#E8FCFA] text-[11px] font-bold text-[#0b7f75]">
    {n}
  </span>
);

// Size a step's textarea to its content so a long step wraps onto more lines
// instead of scrolling horizontally inside a one-line input. Reset to auto first
// so it also shrinks when text is deleted.
const autoGrow = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

const PlanReviewCard: React.FC<PlanReviewCardProps> = ({
  steps,
  etaMinutesMin,
  etaMinutesMax,
  onApprove,
  onRequestChanges,
  onFocusComposer,
  disabled = false,
}) => {
  const [editedSteps, setEditedSteps] = useState<EditableStep[]>(() => toEditable(steps));
  const [removedLabels, setRemovedLabels] = useState<string[]>([]);
  // Resync the editable copy when the underlying plan actually changes — e.g. a
  // session-history refetch replaces this message's plan. Without this the read-only
  // list re-renders with the fresh plan while the editable list stays on the previous
  // one. Keyed on a signature of the steps so an unrelated re-render never wipes edits;
  // a genuinely new plan supersedes any in-progress edits (they were for the old plan).
  const stepsSignature = JSON.stringify(steps.map((s) => [s.id, s.label]));
  useEffect(() => {
    setEditedSteps(toEditable(steps));
    setRemovedLabels([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepsSignature]);
  const addCounterRef = useRef(0);
  const inputRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const pendingFocusKeyRef = useRef<string | null>(null);

  // Focus the input of a freshly added step once it exists in the DOM.
  useEffect(() => {
    const key = pendingFocusKeyRef.current;
    if (!key) return;
    pendingFocusKeyRef.current = null;
    inputRefs.current[key]?.focus();
  }, [editedSteps]);

  // After a resync (session refetch / new plan) the textareas mount with the
  // fresh labels — grow each to fit so pre-existing long steps wrap immediately,
  // not only once the user types in them.
  useEffect(() => {
    Object.values(inputRefs.current).forEach(autoGrow);
  }, [stepsSignature]);

  const etaText = formatEtaRange(etaMinutesMin, etaMinutesMax);

  // Empty (never-filled-in) added steps are ignored everywhere.
  const finalSteps: PlanReviewStep[] = editedSteps
    .map((s) => ({ id: s.id, label: s.label.trim() }))
    .filter((s) => s.label.length > 0);
  const addedLabels = editedSteps
    .filter((s) => s.originalLabel === null && s.label.trim().length > 0)
    .map((s) => s.label.trim());
  const dirty = removedLabels.length > 0 || editedSteps.some(isStepEdited);

  const handleEdit = (key: string, label: string) => {
    setEditedSteps((prev) => prev.map((s) => (s.key === key ? { ...s, label } : s)));
  };

  const handleRemove = (key: string) => {
    const step = editedSteps.find((s) => s.key === key);
    setEditedSteps((prev) => prev.filter((s) => s.key !== key));
    if (step?.originalLabel != null) {
      setRemovedLabels((prev) => [...prev, step.originalLabel as string]);
    }
  };

  const handleAdd = () => {
    addCounterRef.current += 1;
    const key = `new-${addCounterRef.current}`;
    pendingFocusKeyRef.current = key;
    setEditedSteps((prev) => [...prev, { key, id: key, label: '', originalLabel: null }]);
  };

  // Latch against double-clicks: the parent flips to isLoading only after the
  // budget consume resolves, which leaves a window for a second click (= a
  // second consume + a second run). Reset if the send fails and the card
  // becomes actionable again (disabled prop cycles).
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    if (!disabled) setSubmitted(false);
  }, [disabled]);

  const handlePrimary = () => {
    if (finalSteps.length === 0 || submitted) return;
    setSubmitted(true);
    if (dirty) {
      onRequestChanges(composePlanChangeMessage(finalSteps, removedLabels, addedLabels));
    } else {
      onApprove(finalSteps);
    }
  };

  const header = (
    <div className="flex items-center justify-between gap-2.5 border-b border-black/10 px-4 py-3">
      <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-[#0f172a]">
        <span className="h-2 w-2 flex-none rounded-full bg-[#07E5D1]" aria-hidden="true" />
        {CARD_TITLE}
      </span>
      {etaText && (
        <span className="inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full bg-[#E8FCFA] px-2.5 py-1 text-xs font-semibold tabular-nums text-[#0b7f75]">
          <Clock className="h-3 w-3 flex-none" aria-hidden="true" />
          {etaText}
        </span>
      )}
    </div>
  );

  // Older/superseded plan cards: the plan is no longer actionable — show it
  // read-only and collapsed (no inputs, no actions, no hint).
  if (disabled) {
    // Render the plan AS PROPOSED — local edits that were never sent must not
    // masquerade as the plan that ran.
    return (
      <div className="w-full max-w-[560px] overflow-hidden rounded-2xl rounded-bl-md border border-black/10 bg-white shadow-sm">
        {header}
        <ol className="px-3 py-2.5">
          {steps.map((s, i) => (
            <li key={s.id} className="flex items-start gap-2.5 rounded-[10px] px-2 py-1.5">
              <NumberSquare n={i + 1} />
              <div className="min-w-0 flex-1">
                <StepKindLabel kind={s.kind} />
                <div className="text-[13px] leading-snug text-[#64748b]">{s.label}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[560px] overflow-hidden rounded-2xl rounded-bl-md border border-black/10 bg-white shadow-sm">
      {header}
      <p className="px-4 pt-2.5 text-xs leading-relaxed text-[#64748b]">
        {dirty ? CARD_SUBCOPY_EDITED : CARD_SUBCOPY}
      </p>
      <ol className="px-2 pb-1.5 pt-2">
        {editedSteps.map((s, i) => (
          <li
            key={s.key}
            className="group flex items-start gap-2.5 rounded-[10px] px-2 py-1.5 hover:bg-[#f8fafc]"
          >
            <NumberSquare n={i + 1} />
            <div className="min-w-0 flex-1">
              <StepKindLabel kind={s.kind} />
              <textarea
                ref={(el) => {
                  inputRefs.current[s.key] = el;
                  autoGrow(el);
                }}
                rows={1}
                value={s.label}
                onChange={(e) => {
                  handleEdit(s.key, e.target.value);
                  autoGrow(e.target);
                }}
                placeholder={STEP_PLACEHOLDER}
                aria-label={`Plan step ${i + 1}`}
                spellCheck={false}
                className={`w-full resize-none overflow-hidden break-words rounded-md px-1 py-0.5 text-[13px] leading-snug text-[#0f172a] outline-none placeholder:text-[#9CA3AF] focus:bg-white focus:shadow-[0_0_0_2px_rgba(7,229,209,0.35)] ${
                  isStepEdited(s) ? 'bg-[#fffbeb]' : 'bg-transparent'
                }`}
              />
            </div>
            <button
              type="button"
              onClick={() => handleRemove(s.key)}
              title="Remove step"
              aria-label={`Remove step ${i + 1}`}
              className="flex-none rounded-md px-1 py-0.5 text-[15px] leading-none text-[#c2c9d4] opacity-0 transition-opacity hover:bg-[#fef2f2] hover:text-[#ef4444] focus-visible:opacity-100 group-hover:opacity-100"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ol>
      <button
        type="button"
        onClick={handleAdd}
        className="mx-4 mb-1 block w-[calc(100%-2rem)] rounded-[10px] border border-dashed border-[#d3dae3] px-2.5 py-2 text-left text-xs text-[#64748b] transition-colors hover:border-[#07E5D1] hover:bg-[#E8FCFA] hover:text-[#0b7f75]"
      >
        {ADD_STEP_LABEL}
      </button>
      <div className="flex items-center gap-2.5 px-4 pb-1 pt-3">
        <button
          type="button"
          onClick={handlePrimary}
          disabled={finalSteps.length === 0 || submitted}
          className="min-h-[40px] rounded-xl bg-[#07E5D1] px-[18px] py-2.5 text-[13px] font-bold text-[#04352f] transition-colors hover:bg-[#06D4C2] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {dirty ? SEND_CHANGES_LABEL : APPROVE_LABEL}
        </button>
        <button
          type="button"
          onClick={onFocusComposer}
          className="text-xs text-[#64748b] underline underline-offset-[3px] hover:text-[#0f172a]"
        >
          {REQUEST_CHANGES_LABEL}
        </button>
      </div>
      <p className="px-4 pb-3 pt-1 text-[11px] text-[#9CA3AF]">{FOOTER_HINT}</p>
    </div>
  );
};

export default PlanReviewCard;
